/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const BillableActivityFilter = ({ 
  onApplyFilters, 
  onClearFilters,
  currentTab, // 0 = Potenciales, 1 = Facturadas
  activeFilters = [],
  onTabChange // Nueva prop para cambiar el tab desde el filtro
}) => {
  const { t } = useTranslation();

  // Estado local del tab dentro del filtro
  const [localTab, setLocalTab] = useState(currentTab);

  // Estado para el filtro actual que se está construyendo
  const [currentFilter, setCurrentFilter] = useState({
    field: "",
    operator: "",
    value: ""
  });

  // Lista de filtros agregados
  const [filterList, setFilterList] = useState([]);

  // Sincronizar tab local con el prop
  useEffect(() => {
    setLocalTab(currentTab);
  }, [currentTab]);

  // Manejar cambio de tab
  const handleTabSwitch = (newTab) => {
  setLocalTab(newTab);
  if (onTabChange) {
    onTabChange(null, newTab); // Pasar null como event y newTab como valor
  }
  // Limpiar filtros al cambiar de tab
  handleReset();
};

  // Campos disponibles según el tab
  const getAvailableFields = () => {
    const commonFields = [
      { value: "activity_type", label: t("Activity Type"), type: "select" },
      { value: "patient_name", label: t("Patient Name"), type: "text" },
      { value: "doctor_name", label: t("Doctor Name"), type: "text" },
      { value: "service_date", label: t("Service Date"), type: "date" },
      { value: "price", label: t("Price"), type: "number" }
    ];

    if (localTab === 0) {
      // Actividades potenciales
      return [
        ...commonFields,
        { value: "is_billed", label: t("Billed Status"), type: "select" }
      ];
    } else {
      // Actividades facturadas
      return [
        ...commonFields,
        { value: "billing_status", label: t("Billing Status"), type: "select" },
        { value: "code", label: t("Code"), type: "text" },
        { value: "quantity", label: t("Quantity"), type: "number" }
      ];
    }
  };

  // Operadores según el tipo de campo
  const getOperatorsForField = (fieldType) => {
    switch (fieldType) {
      case "text":
        return [
          { value: "contains", label: t("Contains") },
          { value: "equals", label: t("Equals") },
          { value: "starts_with", label: t("Starts with") },
          { value: "ends_with", label: t("Ends with") }
        ];
      case "number":
        return [
          { value: "equals", label: t("Equals") },
          { value: "greater_than", label: t("Greater than") },
          { value: "less_than", label: t("Less than") },
          { value: "between", label: t("Between") }
        ];
      case "date":
        return [
          { value: "equals", label: t("Equals") },
          { value: "before", label: t("Before") },
          { value: "after", label: t("After") },
          { value: "between", label: t("Between") }
        ];
      case "select":
        return [
          { value: "equals", label: t("Equals") },
          { value: "not_equals", label: t("Not equals") }
        ];
      default:
        return [];
    }
  };

  // Opciones para campos tipo select
  const getSelectOptions = (fieldValue) => {
    switch (fieldValue) {
      case "activity_type":
        return [
          { value: "APPOINTMENT", label: t("Appointment") },
          { value: "MEDICAL_TEST", label: t("Medical Test") },
          { value: "DIAGNOSTIC_IMAGING", label: t("Diagnostic Imaging") },
          { value: "IMMUNIZATION", label: t("Immunization") },
          { value: "MEDICINE", label: t("Medicine") },
          { value: "PRESCRIPTION", label: t("Prescription") },
          { value: "TREATMENT", label: t("Treatment") },
          { value: "OTHER", label: t("Other") }
        ];
      case "billing_status":
        return [
          { value: "PENDING", label: t("Pending") },
          { value: "IN_ORDER", label: t("In Order") },
          { value: "INVOICED", label: t("Invoiced") },
          { value: "PAID", label: t("Paid") },
          { value: "CANCELLED", label: t("Cancelled") }
        ];
      case "is_billed":
        return [
          { value: "false", label: t("Unbilled") },
          { value: "true", label: t("Billed") }
        ];
      default:
        return [];
    }
  };

  // Obtener tipo de campo seleccionado
  const getSelectedFieldType = () => {
    const field = getAvailableFields().find(f => f.value === currentFilter.field);
    return field ? field.type : null;
  };

  // Manejar cambio de campo
  const handleFieldChange = (e) => {
    setCurrentFilter({
      field: e.target.value,
      operator: "",
      value: ""
    });
  };

  // Manejar cambio de operador
  const handleOperatorChange = (e) => {
    setCurrentFilter(prev => ({
      ...prev,
      operator: e.target.value
    }));
  };

  // Manejar cambio de valor
  const handleValueChange = (e) => {
    setCurrentFilter(prev => ({
      ...prev,
      value: e.target.value
    }));
  };

  // Agregar filtro a la lista
  const handleAddFilter = () => {
    if (currentFilter.field && currentFilter.operator && currentFilter.value) {
      const field = getAvailableFields().find(f => f.value === currentFilter.field);
      const operator = getOperatorsForField(field.type).find(o => o.value === currentFilter.operator);
      
      const newFilter = {
        ...currentFilter,
        fieldLabel: field.label,
        operatorLabel: operator.label,
        id: Date.now()
      };

      setFilterList(prev => [...prev, newFilter]);
      
      // Limpiar filtro actual
      setCurrentFilter({
        field: "",
        operator: "",
        value: ""
      });
    }
  };

  // Eliminar filtro de la lista
  const handleRemoveFilter = (filterId) => {
    setFilterList(prev => prev.filter(f => f.id !== filterId));
  };

  // Limpiar filtro actual
  const handleClearCurrent = () => {
    setCurrentFilter({
      field: "",
      operator: "",
      value: ""
    });
  };

  // Aplicar todos los filtros
  const handleApplyFilters = () => {
    // Convertir filterList al formato que espera el componente padre
    const formattedFilters = {};
    
    filterList.forEach(filter => {
      switch (filter.field) {
        case "activity_type":
          formattedFilters.activityType = filter.value;
          break;
        case "patient_name":
          formattedFilters.patientName = filter.value;
          break;
        case "doctor_name":
          formattedFilters.doctorName = filter.value;
          break;
        case "billing_status":
          formattedFilters.billingStatus = filter.value;
          break;
        case "is_billed":
          formattedFilters.isBilled = filter.value;
          break;
        case "service_date":
          if (filter.operator === "after") {
            formattedFilters.dateFrom = filter.value;
          } else if (filter.operator === "before") {
            formattedFilters.dateTo = filter.value;
          }
          break;
        case "price":
          if (filter.operator === "greater_than") {
            formattedFilters.minPrice = filter.value;
          } else if (filter.operator === "less_than") {
            formattedFilters.maxPrice = filter.value;
          }
          break;
      }
    });

    onApplyFilters(formattedFilters);
    
    // Cerrar el offcanvas
    const offcanvasElement = document.getElementById('billableActivityAdvFilter');
    const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvasElement);
    if (bsOffcanvas) {
      bsOffcanvas.hide();
    }
  };

  // Reset todos los filtros
  const handleReset = () => {
    setFilterList([]);
    setCurrentFilter({
      field: "",
      operator: "",
      value: ""
    });
    onClearFilters();
  };

  const selectedFieldType = getSelectedFieldType();
  const availableOperators = selectedFieldType ? getOperatorsForField(selectedFieldType) : [];
  const selectOptions = currentFilter.field ? getSelectOptions(currentFilter.field) : [];

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="billableActivityAdvFilter"
      aria-labelledby="billableActivityAdvFilterLabel"
      style={{ width: "420px" }}
    >
      {/* Header */}
      <div className="offcanvas-header" style={{ backgroundColor: "#ff6347" }}>
        <h5 className="offcanvas-title text-white fw-semibold" id="billableActivityAdvFilterLabel">
          {t("Billable Activity Filter")}
        </h5>
        <button
          type="button"
          className="btn-close btn-close-white"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>

      {/* Body */}
      <div className="offcanvas-body bg-light">
        {/* Tab Switch - NUEVO */}
        <div className="mb-4">
          <label className="form-label fw-semibold mb-2">{t("Activity Type")}</label>
          <div className="btn-group w-100" role="group">
            <button
              type="button"
              className={`btn ${localTab === 0 ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleTabSwitch(0)}
              style={{
                backgroundColor: localTab === 0 ? '#ff6347' : 'transparent',
                borderColor: '#ff6347',
                color: localTab === 0 ? '#fff' : '#ff6347'
              }}
            >
              {t("Potential Activities")}
            </button>
            <button
              type="button"
              className={`btn ${localTab === 1 ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleTabSwitch(1)}
              style={{
                backgroundColor: localTab === 1 ? '#ff6347' : 'transparent',
                borderColor: '#ff6347',
                color: localTab === 1 ? '#fff' : '#ff6347'
              }}
            >
              {t("Billed Activities")}
            </button>
          </div>
        </div>

        {/* Select Saved Filter */}
        <div className="mb-4">
          <label className="form-label fw-semibold">{t("Select Saved Filter")}</label>
          <select className="form-select" disabled>
            <option>{t("Select a saved filter template")}</option>
          </select>
        </div>

        {/* Create Filter */}
        <div className="mb-4">
          <h6 className="fw-semibold mb-3" style={{ color: "#ff6347" }}>
            {t("Create Filter")}
          </h6>

          {/* Select Field */}
          <div className="mb-3">
            <label className="form-label fw-semibold">{t("Select Field")}</label>
            <select 
              className="form-select" 
              value={currentFilter.field}
              onChange={handleFieldChange}
            >
              <option value="">{t("Select Field")}</option>
              {getAvailableFields().map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
          </div>

          {/* Operator - Solo se muestra si hay un campo seleccionado */}
          {currentFilter.field && (
            <div className="mb-3">
              <label className="form-label fw-semibold">{t("Operator")}</label>
              <select 
                className="form-select"
                value={currentFilter.operator}
                onChange={handleOperatorChange}
              >
                <option value="">{t("Select Operator")}</option>
                {availableOperators.map(op => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Value - Solo se muestra si hay operador seleccionado */}
          {currentFilter.field && currentFilter.operator && (
            <div className="mb-3">
              <label className="form-label fw-semibold">{t("Value")}</label>
              {selectedFieldType === "select" ? (
                <select 
                  className="form-select"
                  value={currentFilter.value}
                  onChange={handleValueChange}
                >
                  <option value="">{t("Select Value")}</option>
                  {selectOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : selectedFieldType === "date" ? (
                <input
                  type="date"
                  className="form-control"
                  value={currentFilter.value}
                  onChange={handleValueChange}
                />
              ) : selectedFieldType === "number" ? (
                <input
                  type="number"
                  className="form-control"
                  value={currentFilter.value}
                  onChange={handleValueChange}
                  placeholder="0"
                  step="0.01"
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={currentFilter.value}
                  onChange={handleValueChange}
                  placeholder={t("Enter value")}
                />
              )}
            </div>
          )}

          {/* Botones Add y Clear */}
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleAddFilter}
              disabled={!currentFilter.field || !currentFilter.operator || !currentFilter.value}
              style={{ borderColor: "#ff6347", color: "#ff6347" }}
            >
              {t("Add in Filter")}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClearCurrent}
            >
              {t("Clear")}
            </button>
          </div>
        </div>

        {/* Lista de filtros agregados */}
        {filterList.length > 0 && (
          <div className="mb-3">
            <h6 className="fw-semibold mb-3">{t("Active Filters")} ({filterList.length})</h6>
            <div className="d-flex flex-column gap-2">
              {filterList.map(filter => (
                <div 
                  key={filter.id} 
                  className="bg-white border rounded p-2 d-flex justify-content-between align-items-center"
                >
                  <div className="small">
                    <span className="fw-semibold text-primary">{filter.fieldLabel}</span>
                    {" "}<span className="text-muted">{filter.operatorLabel}</span>{" "}
                    <span className="fw-semibold">{filter.value}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger p-0"
                    onClick={() => handleRemoveFilter(filter.id)}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>
                      close
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="offcanvas-footer border-top p-3 bg-white">
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary flex-fill"
            onClick={handleReset}
          >
            {t("Reset")}
          </button>
          <button
            type="button"
            className="btn flex-fill text-white"
            onClick={handleApplyFilters}
            style={{ backgroundColor: "#ff6347" }}
            disabled={filterList.length === 0}
          >
            {t("Apply Filter")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillableActivityFilter;